import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { PostgridSubNavigation } from '@/components/postgrid/common/PostgridSubNavigation';
import { ContactTable } from '@/components/postgrid/contacts/ContactTable';
import { Heading } from '@/components/ui/catalyst/heading';
import type { ReadPostgridContact } from '@/services/api/generated/postgridService';
import {
  usePostgridContactsDestroyMutation,
  usePostgridContactsListQuery,
} from '@/services/api/postgridService';

const ContactManagementPage: React.FC = () => {
  const navigate = useNavigate();

  // Fetch contacts
  const {
    data: contactsResponse,
    isLoading,
    error,
    refetch,
  } = usePostgridContactsListQuery(
    { isActive: true, limit: 100 },
    { refetchOnMountOrArgChange: true },
  );

  const contacts = contactsResponse?.results || [];

  // Mutations
  const [deleteContact] = usePostgridContactsDestroyMutation();

  const handleContactView = (contact: ReadPostgridContact) => {
    navigate(`/postgrid/contacts/${contact.id}`);
  };

  const handleContactDelete = async (contact: ReadPostgridContact) => {
    try {
      await deleteContact({ id: contact.id }).unwrap();
      toast.success('Contact deleted successfully');
      refetch();
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  return (
    <div
      className="flex flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950"
      style={{ height: '100vh', contain: 'layout style' }}
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-zinc-200 dark:bg-zinc-900 dark:border-zinc-800">
        <div className="px-6 py-4">
          <Heading className="text-zinc-900 dark:text-white font-semibold">
            Contacts
          </Heading>
        </div>
        <PostgridSubNavigation className="px-6" />
      </div>

      {/* Content */}
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden p-6">
        <ContactTable
          contacts={contacts}
          isLoading={isLoading}
          error={error ? 'Failed to load contacts' : undefined}
          onContactView={handleContactView}
          onContactDelete={handleContactDelete}
        />
      </div>
    </div>
  );
};

export default ContactManagementPage;
